/**
 * IDD Schema Store
 *
 * Zustand store for managing the EnergyPlus IDD schema.
 * Provides schema loading, caching, and lookup functionality.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import {
  type IDDSchema,
  type IDDObjectType,
  type IDDField,
  type IDDParseError,
  parseIDD,
  createEmptySchema,
} from '../idd';

interface IDDState {
  /** The parsed IDD schema */
  schema: IDDSchema;

  /** Whether the schema has been loaded */
  isLoaded: boolean;

  /** Whether schema is currently loading */
  isLoading: boolean;

  /** Parse errors from loading the schema */
  errors: IDDParseError[];

  /** EnergyPlus version of the loaded schema */
  version: string;

  // Actions

  /** Load and parse IDD content */
  loadSchema: (content: string) => void;

  /** Load schema from a URL */
  loadSchemaFromUrl: (url: string) => Promise<void>;

  /** Clear the current schema */
  clearSchema: () => void;

  /** Get an object type definition by name (case-insensitive) */
  getObjectType: (name: string) => IDDObjectType | undefined;

  /** Get all object type names */
  getObjectTypeNames: () => string[];

  /** Get object types in a specific group */
  getObjectTypesByGroup: (groupName: string) => IDDObjectType[];

  /** Get field definition for an object and field index */
  getField: (objectTypeName: string, fieldIndex: number) => IDDField | undefined;

  /** Get all valid choices for a choice-type field */
  getFieldChoices: (objectTypeName: string, fieldIndex: number) => string[];

  /** Get objects that can be referenced by a given object-list name */
  getReferencedObjectTypes: (objectListName: string) => string[];

  /** Check if an object type exists */
  hasObjectType: (name: string) => boolean;

  /** Search object types by name pattern */
  searchObjectTypes: (query: string) => IDDObjectType[];
}

export const useIDDStore = create<IDDState>()(
  devtools(
    // Note: Persistence disabled intentionally - IDD schema is large and should be
    // loaded fresh from URL each session. The schema data contains Map objects that
    // don't serialize well, and re-parsing is fast enough.
    (set, get) => ({
        schema: createEmptySchema(),
        isLoaded: false,
        isLoading: false,
        errors: [],
        version: '',

        loadSchema: (content: string) => {
          set({ isLoading: true, errors: [] });

          try {
            const result = parseIDD(content);

            if (result.schema) {
              set({
                schema: result.schema,
                isLoaded: true,
                isLoading: false,
                errors: result.errors,
                version: result.schema.version,
              });
            } else {
              set({
                isLoading: false,
                errors: result.errors,
              });
            }
          } catch (error) {
            set({
              isLoading: false,
              errors: [
                {
                  message: error instanceof Error ? error.message : 'Unknown error loading schema',
                  line: 0,
                  column: 0,
                },
              ],
            });
          }
        },

        loadSchemaFromUrl: async (url: string) => {
          set({ isLoading: true, errors: [] });

          try {
            const response = await fetch(url);
            if (!response.ok) {
              throw new Error(`Failed to fetch IDD: ${response.statusText}`);
            }
            const content = await response.text();
            get().loadSchema(content);
          } catch (error) {
            set({
              isLoading: false,
              errors: [
                {
                  message: error instanceof Error ? error.message : 'Unknown error fetching schema',
                  line: 0,
                  column: 0,
                },
              ],
            });
          }
        },

        clearSchema: () => {
          set({
            schema: createEmptySchema(),
            isLoaded: false,
            isLoading: false,
            errors: [],
            version: '',
          });
        },

        getObjectType: (name: string) => {
          const { schema } = get();
          return schema.objectTypes.get(name.toLowerCase());
        },

        getObjectTypeNames: () => {
          const { schema } = get();
          return Array.from(schema.objectTypes.values()).map((ot) => ot.name);
        },

        getObjectTypesByGroup: (groupName: string) => {
          const { schema } = get();
          const group = schema.groups.find((g) => g.name === groupName);
          if (!group) return [];

          return group.objectTypes
            .map((name) => schema.objectTypes.get(name.toLowerCase()))
            .filter((ot): ot is IDDObjectType => ot !== undefined);
        },

        getField: (objectTypeName: string, fieldIndex: number) => {
          const objectType = get().getObjectType(objectTypeName);
          if (!objectType || fieldIndex < 0 || fieldIndex >= objectType.fields.length) {
            return undefined;
          }
          return objectType.fields[fieldIndex];
        },

        getFieldChoices: (objectTypeName: string, fieldIndex: number) => {
          const field = get().getField(objectTypeName, fieldIndex);
          if (!field) return [];

          // Direct choices
          if (field.choices && field.choices.length > 0) {
            return field.choices;
          }

          // Autosizable/autocalculatable options
          const choices: string[] = [];
          if (field.autosizable) {
            choices.push('autosize');
          }
          if (field.autocalculatable) {
            choices.push('autocalculate');
          }

          return choices;
        },

        getReferencedObjectTypes: (objectListName: string) => {
          const { schema } = get();
          const objectTypes = schema.referenceLists.get(objectListName);
          return objectTypes ? Array.from(objectTypes) : [];
        },

        hasObjectType: (name: string) => {
          const { schema } = get();
          return schema.objectTypes.has(name.toLowerCase());
        },

        searchObjectTypes: (query: string) => {
          const { schema } = get();
          const q = query.toLowerCase();

          const matches = Array.from(schema.objectTypes.values()).filter(
            (ot) =>
              ot.name.toLowerCase().includes(q) ||
              ot.memo.toLowerCase().includes(q)
          );

          // Sort by relevance: exact match > prefix > word-boundary > substring > memo-only
          return matches.sort((a, b) => {
            const aName = a.name.toLowerCase();
            const bName = b.name.toLowerCase();
            const aScore = aName === q
              ? 0
              : aName.startsWith(q)
                ? 1
                : aName.includes(':' + q) || aName.includes(' ' + q)
                  ? 2
                  : aName.includes(q)
                    ? 3
                    : 4;
            const bScore = bName === q
              ? 0
              : bName.startsWith(q)
                ? 1
                : bName.includes(':' + q) || bName.includes(' ' + q)
                  ? 2
                  : bName.includes(q)
                    ? 3
                    : 4;
            if (aScore !== bScore) return aScore - bScore;
            return a.name.localeCompare(b.name);
          });
        },
      }),
    { name: 'IDDStore' }
  )
);

/**
 * Selector hooks for common queries
 */
export const useObjectType = (name: string) => {
  return useIDDStore((state) => state.getObjectType(name));
};

export const useObjectTypeNames = () => {
  return useIDDStore((state) =>
    state.isLoaded ? state.getObjectTypeNames() : []
  );
};

export const useSchemaGroups = () => {
  return useIDDStore((state) => state.schema.groups);
};

export const useSchemaVersion = () => {
  return useIDDStore((state) => state.version);
};

export const useSchemaIsLoaded = () => {
  return useIDDStore((state) => state.isLoaded);
};
